import React, { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Box, Paper, Typography, Button, Grid, TextField, MenuItem,
  Chip, Stack, Table, TableBody, TableCell, TableHead, TableRow,
  IconButton, Alert, CircularProgress, Tooltip, Dialog,
  DialogTitle, DialogContent, DialogActions, Checkbox, ListItemText,
} from '@mui/material';
import HomeWorkIcon        from '@mui/icons-material/HomeWork';
import SendIcon            from '@mui/icons-material/Send';
import DeleteOutlineIcon   from '@mui/icons-material/DeleteOutline';
import EmailIcon           from '@mui/icons-material/Email';
import EventIcon           from '@mui/icons-material/Event';
import PersonIcon          from '@mui/icons-material/Person';
import CheckCircleIcon     from '@mui/icons-material/CheckCircle';
import BlockIcon           from '@mui/icons-material/Block';
import AddIcon             from '@mui/icons-material/Add';
import CloseIcon           from '@mui/icons-material/Close';
import EditIcon            from '@mui/icons-material/Edit';
import MarkEmailReadIcon   from '@mui/icons-material/MarkEmailRead';
import ThumbUpIcon         from '@mui/icons-material/ThumbUp';
import ThumbDownIcon       from '@mui/icons-material/ThumbDown';
import HourglassEmptyIcon  from '@mui/icons-material/HourglassEmpty';
import WorkIcon            from '@mui/icons-material/Work';
import globals             from '../../services/globals';
import { authFetch }       from '../../services/apiServices';
import { isActiveWorker, activeWorkers } from '../../utils/workerStatus';


/**
 * Kush mund të zgjidhet për një leje — aktivë, të ngushtuar nga filiali dhe
 * departamenti.
 *
 * Departamenti kërkohet te TË DYJA fushat: `Department` (i pari, ai që shkon te
 * kolona `department_id`) dhe `DepartmentIds` (të tjerët). Një punëtor i ndarë
 * mes dy departamenteve ka të parin te njëra fushë dhe pjesën tjetër te lista —
 * po e kërkuam vetëm njërën, ai zhduket nga gjysma e filtrave.
 *
 * Në nivel moduli dhe i eksportuar me qëllim: rregulli i zgjedhjes provohet pa
 * ngritur dialogun (shih `remoteWorkPicker.test.js`).
 */
export function filterPickableWorkers(workers, branchId = '', deptId = '') {
  return activeWorkers(workers).filter((w) => {
    if (branchId && String(w.Branch ?? w.branch_id ?? '') !== String(branchId)) return false;
    if (deptId) {
      const ids = [w.Department ?? w.department_id, ...(w.DepartmentIds || w.department_ids || [])];
      if (!ids.filter((x) => x != null).map(String).includes(String(deptId))) return false;
    }
    return true;
  });
}

// Pulls the JWT from sessionStorage (primary) or cookie (rememberMe fallback) —
// same pattern used by apiServices.js so all calls share the same auth source.

// Përmes authFetch: token-i rinovohet kur skadon (pas 30 minutash kjo faqe
// kthente 401 për çdo veprim), dhe trupi i gabimit lexohet edhe kur s'është JSON.
const httpFetch = async (path, opts = {}) => {
  const res = await authFetch(path, {
    ...opts,
    headers: { 'Content-Type': 'application/json', ...(opts.headers || {}) },
  });
  return res.json().catch(() => ({ success: false, error: `HTTP ${res.status}` }));
};

function toISODate(d) {
  if (!d) return '';
  if (d instanceof Date) return d.toISOString().slice(0, 10);
  const s = String(d);
  return s.slice(0, 10);
}

function fmtDate(d) {
  if (!d) return '—';
  const iso = toISODate(d);
  if (!iso) return '—';
  return new Date(iso + 'T12:00:00').toLocaleDateString('sq-AL', {
    weekday: 'short', day: '2-digit', month: 'short', year: 'numeric',
  });
}

function isExpired(dateStr) {
  const iso = toISODate(dateStr);
  return new Date(iso + 'T23:59:59') < new Date();
}

function permitStatus(permit) {
  if (permit.status === 'pending')  return 'pending';
  if (permit.status === 'rejected') return 'rejected';
  if (permit.revoked_at)            return 'revoked';
  const hasFuture = (permit.allowed_dates || []).map(toISODate).some(d => d && !isExpired(d));
  return hasFuture ? 'active' : 'expired';
}

// ── Date Multi-Picker ─────────────────────────────────────────────────────────
function DateMultiPicker({ selected, onChange }) {
  const { t } = useTranslation();
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return new Date(d.getFullYear(), d.getMonth(), 1);
  });

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  const daysInMonth   = new Date(month.getFullYear(), month.getMonth() + 1, 0).getDate();
  const firstDay      = new Date(month.getFullYear(), month.getMonth(), 1).getDay();
  const adjustedFirst = (firstDay === 0 ? 6 : firstDay - 1);

  const toISO = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  };

  const toggleDate = (day) => {
    const date = new Date(month.getFullYear(), month.getMonth(), day);
    if (date < today) return;
    const iso = toISO(date);
    onChange(selected.includes(iso) ? selected.filter(d => d !== iso) : [...selected, iso]);
  };

  const prevMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() - 1, 1));
  const nextMonth = () => setMonth(new Date(month.getFullYear(), month.getMonth() + 1, 1));
  const monthName = month.toLocaleDateString('sq-AL', { month: 'long', year: 'numeric' });
  const weekDays  = [t('remoteWork.monShort'), t('remoteWork.tueShort'), t('remoteWork.wedShort'), t('remoteWork.thuShort'), t('remoteWork.friShort'), t('remoteWork.satShort'), t('remoteWork.sunShort')];

  return (
    <Box sx={{ bgcolor: 'background.paper', border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1.5 }}>
        <IconButton size="small" onClick={prevMonth}>‹</IconButton>
        <Typography variant="subtitle2" fontWeight={700} textTransform="capitalize">{monthName}</Typography>
        <IconButton size="small" onClick={nextMonth}>›</IconButton>
      </Stack>

      <Grid container columns={7} sx={{ mb: 0.5 }}>
        {weekDays.map(d => (
          <Grid item key={d} xs={1}>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', fontWeight: 700 }}>{d}</Typography>
          </Grid>
        ))}
      </Grid>

      <Grid container columns={7}>
        {Array.from({ length: adjustedFirst }).map((_, i) => <Grid item key={`e-${i}`} xs={1} />)}
        {Array.from({ length: daysInMonth }).map((_, i) => {
          const day  = i + 1;
          const date = new Date(month.getFullYear(), month.getMonth(), day);
          const iso  = toISO(date);
          const past = date < today;
          const sel  = selected.includes(iso);
          const isWeekend = date.getDay() === 0 || date.getDay() === 6;
          return (
            <Grid item key={day} xs={1} sx={{ p: 0.25 }}>
              <Box onClick={() => !past && toggleDate(day)} sx={{
                width: '100%', aspectRatio: '1', display: 'flex', alignItems: 'center', justifyContent: 'center',
                borderRadius: '50%', cursor: past ? 'not-allowed' : 'pointer',
                bgcolor: sel ? 'primary.main' : 'transparent',
                color: sel ? '#fff' : past ? 'text.disabled' : isWeekend ? 'warning.main' : 'text.primary',
                fontWeight: sel ? 700 : 400, fontSize: '0.8rem',
                '&:hover': !past ? { bgcolor: sel ? 'primary.dark' : 'primary.50' } : {},
                transition: 'all 0.15s',
              }}>{day}</Box>
            </Grid>
          );
        })}
      </Grid>

      {selected.length > 0 && (
        <Box sx={{ mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
          <Stack direction="row" flexWrap="wrap" gap={0.5}>
            {[...selected].sort().map(d => (
              <Chip key={d} label={fmtDate(d)} size="small"
                onDelete={() => onChange(selected.filter(x => x !== d))}
                color="primary" variant="outlined" sx={{ fontSize: '0.72rem' }} />
            ))}
          </Stack>
        </Box>
      )}
    </Box>
  );
}

// ── Main Component ────────────────────────────────────────────────────────────
export default function RemoteWork() {
  const { t } = useTranslation();
  const [permits, setPermits]       = useState([]);
  const [workers, setWorkers]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [saving, setSaving]         = useState(false);
  const [snack, setSnack]           = useState('');
  const [snackErr, setSnackErr]     = useState(false);
  const [tab, setTab]               = useState(0); // 0=aktive, 1=kerkesa, 2=te gjitha
  const [createOpen, setCreateOpen] = useState(false);
  const [requestOpen, setRequestOpen] = useState(false);
  const [editOpen, setEditOpen]     = useState(false);
  const [editPermit, setEditPermit] = useState(null);
  const [editDates, setEditDates]   = useState([]);
  const [editNotes, setEditNotes]   = useState('');
  const [resending, setResending]   = useState(null);
  const [rejectDialog, setRejectDialog] = useState(null); // permit being rejected
  const [rejectReason, setRejectReason] = useState('');
  const [revokeTarget, setRevokeTarget] = useState(null); // permit being revoked
  const [revoking, setRevoking] = useState(false);

  // Create form
  // Një leje për një njeri ishte puna e dyfishtë e çdo fillimi muaji: e njëjta
  // listë datash i jepej ekipit njeri-për-njeri. Tani zgjidhen disa njëherësh,
  // dhe filiali/departamenti shërbejnë si zgjedhës grupi.
  const [selWorkers, setSelWorkers] = useState([]);
  const [fBranch,   setFBranch]     = useState('');
  const [fDept,     setFDept]       = useState('');
  const [departments, setDepartments] = useState([]);
  const [branches,  setBranches]    = useState([]);
  const [selDates,  setSelDates]    = useState([]);
  const [notes,     setNotes]       = useState('');
  const [sendEmail, setSendEmail]   = useState(true);

  // Request form (worker submits)
  const [reqDates, setReqDates]     = useState([]);
  const [reqNotes, setReqNotes]     = useState('');

  // Self-identification for workers without linked worker_id
  const [selfWorkerId, setSelfWorkerId] = useState(() => localStorage.getItem('rw_self_worker_id') || '');
  const [selfPickerVal, setSelfPickerVal] = useState('');

  // Current user info
  const currentUser = globals().getLogedInUser();
  const roleNames = (currentUser?.data?.roles || currentUser?.roles || [])
    .map(r => (r?.name || r?.role_name || r || '').toString().toLowerCase().trim())
    .filter(Boolean);
  const hasAdminRole = roleNames.some(r => r === 'admin' || r === 'menaxher' || r === 'manager');
  const isAdmin = hasAdminRole || currentUser?.data?.role === 'admin' || currentUser?.role === 'admin' || currentUser?.data?.level === 1;

  const myWorker = (() => {
    const wid = currentUser?.data?.worker_id;
    const email = (currentUser?.data?.email || currentUser?.email || '').toLowerCase();
    if (wid) return workers.find(w => String(w.Id || w.id) === String(wid));
    const byEmail = email ? workers.find(w => (w.Email || w.email || '').toLowerCase() === email) : null;
    if (byEmail) return byEmail;
    if (selfWorkerId) return workers.find(w => String(w.Id || w.id) === selfWorkerId) || null;
    return null;
  })();
  const myWorkerName = myWorker
    ? (`${myWorker.FirstName || myWorker.Name || myWorker.first_name || myWorker.name || ''} `
       + `${myWorker.LastName || myWorker.Surname || myWorker.last_name || myWorker.surname || ''}`).trim()
    : (currentUser?.data?.name || currentUser?.name || '');

  const handleSelfIdentify = () => {
    if (!selfPickerVal) return;
    localStorage.setItem('rw_self_worker_id', selfPickerVal);
    setSelfWorkerId(selfPickerVal);
    setSelfPickerVal('');
  };

  const showSnack = (msg, err = false) => { setSnack(msg); setSnackErr(err); setTimeout(() => setSnack(''), 4500); };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [pr, wr, dr, br] = await Promise.all([
        httpFetch('/api/remote-work'),
        httpFetch('/api/workers'),
        httpFetch('/api/departments').catch(() => ({ data: [] })),
        httpFetch('/api/branches').catch(() => ({ data: [] })),
      ]);
      setPermits(pr?.data || []);
      const wList = Array.isArray(wr?.data) ? wr.data : Array.isArray(wr) ? wr : [];
      setWorkers(wList);
      setDepartments(Array.isArray(dr?.data) ? dr.data : []);
      setBranches(Array.isArray(br?.data) ? br.data : []);
    } catch { showSnack(t('remoteWork.errorLoad'), true); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Derived lists
  const activePermits  = permits.filter(p => ['active', 'expired'].includes(permitStatus(p)));
  const pendingPermits = permits.filter(p => permitStatus(p) === 'pending');
  const allPermits     = permits;
  const visibleList    = tab === 0 ? activePermits : tab === 1 ? pendingPermits : allPermits;

  // Emri lexohej si `w.Name`/`w.Surname` — fusha që API-ja nuk i kthen. Prandaj
  // te lista rënëse dilte vetëm email-i në kllapa, pa asnjë emër para tij.
  const wName = (w) => `${w?.FirstName || w?.Name || w?.first_name || w?.name || ''} `
    + `${w?.LastName || w?.Surname || w?.last_name || w?.surname || ''}`.trim();
  const wId = (w) => String(w?.Id ?? w?.id ?? '');

  // Kandidatët e lejes: aktivë, të ngushtuar nga filiali dhe departamenti.
  const pickableWorkers = filterPickableWorkers(workers, fBranch, fDept);

  const selectedWorkerObjs = selWorkers
    .map((id) => workers.find((w) => wId(w) === String(id)))
    .filter(Boolean);

  const toggleWorker = (id) => setSelWorkers((prev) => (
    prev.includes(String(id)) ? prev.filter((x) => x !== String(id)) : [...prev, String(id)]
  ));

  // ── Admin: create approved permit ─────────────────────────────────────────
  // Një kërkesë për çdo punëtor, jo një transaksion i përbashkët: leja e njërit
  // s'ka pse të bjerë sepse email-i i tjetrit dështoi. Përfundimi thuhet me
  // numra, dhe kush dështoi mbetet i zgjedhur që të provohet sërish.
  const handleCreate = async () => {
    if (!selWorkers.length) { showSnack(t('remoteWork.selectWorkerRequired'), true); return; }
    if (!selDates.length) { showSnack(t('remoteWork.selectDateRequired'), true); return; }
    setSaving(true);
    const failed = [];
    let created = 0;
    let firstError = '';
    // Leja u krijua, por email-i jo — s'është dështim i lejes, por njeriu
    // duhet ta dijë: punëtori s'do të marrë asgjë.
    let mailFailed = 0;
    let mailError = '';
    try {
      for (const w of selectedWorkerObjs) {
        try {
          const r = await httpFetch('/api/remote-work', {
            method: 'POST',
            body: JSON.stringify({
              worker_id: wId(w),
              worker_name: wName(w),
              worker_email: w.Email || w.email || '',
              allowed_dates: selDates,
              notes,
              send_email: sendEmail,
            }),
          });
          if (r?.success) {
            created++;
            if (r.emailSent === false) { mailFailed++; mailError = mailError || r.emailError || ''; }
          }
          else { failed.push(wId(w)); firstError = firstError || r?.error || ''; }
        } catch (e) { failed.push(wId(w)); firstError = firstError || e?.message || ''; }
      }

      if (created && !failed.length && mailFailed) {
        showSnack(`${created === 1 ? 'Leja u krijua' : `U krijuan ${created} leje`}, por email-i nuk u dërgua${mailFailed < created ? ` te ${mailFailed}` : ''}${mailError ? `: ${mailError}` : '.'}`, true);
        setCreateOpen(false); setSelWorkers([]); setSelDates([]); setNotes('');
        setFBranch(''); setFDept('');
      } else if (created && !failed.length) {
        showSnack(created === 1 ? t('remoteWork.permitCreated') : `U krijuan ${created} leje.`);
        setCreateOpen(false); setSelWorkers([]); setSelDates([]); setNotes('');
        setFBranch(''); setFDept('');
      } else if (created) {
        showSnack(`U krijuan ${created} leje; ${failed.length} dështuan${firstError ? `: ${firstError}` : '.'}`, true);
        setSelWorkers(failed);
      } else {
        showSnack(firstError ? `Leja nuk u krijua: ${firstError}` : t('common.error'), true);
      }
      load();
    } finally { setSaving(false); }
  };

  // ── Worker/Leader: submit request ─────────────────────────────────────────
  const handleRequest = async () => {
    if (!reqDates.length) { showSnack(t('remoteWork.selectDateRequired'), true); return; }
    if (!myWorker) { showSnack(t('remoteWork.accountNotLinked'), true); return; }
    setSaving(true);
    try {
      const wid   = String(myWorker.Id || myWorker.id);
      const wname = myWorkerName;
      const wemail= myWorker.Email || myWorker.email || '';
      const role  = isAdmin ? 'admin' : 'worker';
      const r = await httpFetch('/api/remote-work/request', {
        method: 'POST',
        body: JSON.stringify({ worker_id: wid, worker_name: wname, worker_email: wemail, allowed_dates: reqDates, notes: reqNotes, requested_by: wname, requested_by_role: role }),
      });
      if (r?.success) {
        showSnack(t('remoteWork.requestSent'));
        setRequestOpen(false); setReqDates([]); setReqNotes('');
        load();
        setTab(1);
      } else showSnack(r?.error || t('common.error'), true);
    } catch { showSnack(t('remoteWork.errorSend'), true); }
    finally { setSaving(false); }
  };

  // ── Approve ───────────────────────────────────────────────────────────────
  const handleApprove = async (permit) => {
    setSaving(true);
    try {
      const approvedBy = currentUser?.data?.name || currentUser?.name || 'Admin';
      const r = await httpFetch(`/api/remote-work/${permit.id}/approve`, {
        method: 'PUT',
        body: JSON.stringify({ approved_by: approvedBy, send_email: !!permit.worker_email }),
      });
      if (r?.success && r.emailSent === false) {
        showSnack(`Kërkesa u aprovua, por email-i nuk u dërgua${r.emailError ? `: ${r.emailError}` : '.'}`, true);
        load();
      } else if (r?.success) {
        showSnack(r.emailSent ? t('remoteWork.requestApprovedEmail') : t('remoteWork.requestApproved'));
        load();
      } else showSnack(r?.error || t('common.error'), true);
    } catch { showSnack(t('remoteWork.errorApprove'), true); }
    finally { setSaving(false); }
  };

  // ── Reject ────────────────────────────────────────────────────────────────
  const handleReject = async () => {
    if (!rejectDialog) return;
    setSaving(true);
    try {
      const rejectedBy = currentUser?.data?.name || currentUser?.name || 'Admin';
      const r = await httpFetch(`/api/remote-work/${rejectDialog.id}/reject`, {
        method: 'PUT',
        body: JSON.stringify({ rejected_by: rejectedBy, reason: rejectReason }),
      });
      if (r?.success) {
        showSnack(t('remoteWork.requestRejected'));
        setRejectDialog(null); setRejectReason('');
        load();
      } else showSnack(r?.error || t('common.error'), true);
    } catch { showSnack(t('remoteWork.errorReject'), true); }
    finally { setSaving(false); }
  };

  // ── Edit ──────────────────────────────────────────────────────────────────
  const openEdit = (permit) => {
    setEditPermit(permit);
    setEditDates((permit.allowed_dates || []).map(toISODate).filter(Boolean));
    setEditNotes(permit.notes || '');
    setEditOpen(true);
  };

  const handleEdit = async () => {
    if (!editDates.length) { showSnack(t('remoteWork.selectDateRequired'), true); return; }
    setSaving(true);
    try {
      const r = await httpFetch(`/api/remote-work/${editPermit.id}`, {
        method: 'PUT',
        body: JSON.stringify({ allowed_dates: editDates, notes: editNotes }),
      });
      if (r?.success) { showSnack(t('remoteWork.permitUpdated')); setEditOpen(false); load(); }
      else showSnack(r?.error || t('common.error'), true);
    } catch { showSnack(t('remoteWork.errorSave'), true); }
    finally { setSaving(false); }
  };

  // ── Resend email ──────────────────────────────────────────────────────────
  const handleResendEmail = async (permit) => {
    if (!permit.worker_email) { showSnack(t('remoteWork.noWorkerEmail'), true); return; }
    setResending(permit.id);
    try {
      const r = await httpFetch(`/api/remote-work/${permit.id}/resend-email`, { method: 'POST' });
      if (r?.success) { showSnack(t('remoteWork.emailResent', { email: permit.worker_email })); load(); }
      else showSnack(r?.error || t('remoteWork.errorSend'), true);
    } catch { showSnack(t('remoteWork.errorSend'), true); }
    finally { setResending(null); }
  };

  // ── Revoke ────────────────────────────────────────────────────────────────
  // Click on the trash icon → opens the styled confirm dialog below.
  // The actual DELETE happens in `executeRevoke` once the user confirms.
  const handleRevoke = (permit) => setRevokeTarget(permit);

  const executeRevoke = async () => {
    if (!revokeTarget) return;
    setRevoking(true);
    try {
      await httpFetch(`/api/remote-work/${revokeTarget.id}`, { method: 'DELETE' });
      showSnack(t('remoteWork.permitRevoked'));
      setRevokeTarget(null);
      load();
    } catch {
      showSnack(t('common.error'), true);
    } finally {
      setRevoking(false);
    }
  };

  // ── Status chip ───────────────────────────────────────────────────────────
  const statusChip = (p) => {
    const s = permitStatus(p);
    if (s === 'pending')  return <Chip size="small" label={t('remoteWork.statusPending')}  color="warning" icon={<HourglassEmptyIcon />} />;
    if (s === 'rejected') return <Chip size="small" label={t('remoteWork.statusRejected')} color="error"   icon={<BlockIcon />} />;
    if (s === 'active')   return <Chip size="small" label={t('remoteWork.statusActive')}   color="success"  icon={<CheckCircleIcon />} />;
    if (s === 'expired')  return <Chip size="small" label={t('remoteWork.statusExpired')}  color="default" />;
    return                       <Chip size="small" label={t('remoteWork.statusRevoked')}  color="error"   icon={<BlockIcon />} />;
  };

  // ── Stats ─────────────────────────────────────────────────────────────────
  const activeCount  = permits.filter(p => permitStatus(p) === 'active').length;
  const pendingCount = pendingPermits.length;
  const today        = new Date().toISOString().slice(0, 10);
  const todayCount   = permits.filter(p =>
    !p.revoked_at && (p.status === 'approved' || !p.status) &&
    (p.allowed_dates || []).map(toISODate).includes(today)
  ).length;

  // ── Permit row ────────────────────────────────────────────────────────────
  const PermitRow = ({ p }) => {
    const dates = (p.allowed_dates || []).map(toISODate).filter(Boolean).sort();
    const st    = permitStatus(p);
    return (
      <TableRow key={p.id} hover sx={{ opacity: ['revoked','rejected'].includes(st) ? 0.55 : 1 }}>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={0.8}>
            <PersonIcon fontSize="small" color="action" />
            <Box>
              <Typography variant="body2" fontWeight={600}>{p.worker_name || p.worker_full || '—'}</Typography>
              {p.worker_email && <Typography variant="caption" color="text.secondary">{p.worker_email}</Typography>}
              {p.requested_by_role && p.requested_by_role !== 'admin' && (
                <Chip size="small" label={p.requested_by_role === 'leader' ? t('remoteWork.requestFromLeader') : t('remoteWork.requestFromWorker')}
                  sx={{ fontSize: '0.62rem', height: 16, mt: 0.3 }} color="info" variant="outlined" />
              )}
            </Box>
          </Stack>
        </TableCell>
        <TableCell sx={{ maxWidth: 200 }}>
          <Stack direction="row" flexWrap="wrap" gap={0.4}>
            {dates.slice(0, 4).map(d => (
              <Chip key={d} label={fmtDate(d)} size="small"
                color={isExpired(d) ? 'default' : 'primary'}
                variant={isExpired(d) ? 'outlined' : 'filled'}
                sx={{ fontSize: '0.68rem', height: 20 }} />
            ))}
            {dates.length > 4 && <Chip label={`+${dates.length - 4}`} size="small" sx={{ height: 20, fontSize: '0.68rem' }} />}
          </Stack>
        </TableCell>
        <TableCell>{statusChip(p)}</TableCell>
        <TableCell>
          {p.approved_by ? (
            <Typography variant="caption" color="text.secondary">
              {p.status === 'rejected' ? '✗ ' : '✓ '}{p.approved_by}
            </Typography>
          ) : '—'}
          {p.reject_reason && (
            <Typography variant="caption" color="error.main" display="block">
              {p.reject_reason}
            </Typography>
          )}
        </TableCell>
        <TableCell sx={{ maxWidth: 120 }}>
          <Typography variant="caption" color="text.secondary">{p.notes || '—'}</Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" alignItems="center" gap={0.3}>
            {p.email_sent
              ? <Tooltip title={t('remoteWork.emailSent')}><EmailIcon fontSize="small" color="success" /></Tooltip>
              : <Tooltip title={t('remoteWork.emailNotSent')}><EmailIcon fontSize="small" color="disabled" /></Tooltip>}
            {p.worker_email && !['revoked','rejected'].includes(st) && (
              <Tooltip title={t('remoteWork.resendEmail')}>
                <IconButton size="small" color="primary" onClick={() => handleResendEmail(p)} disabled={resending === p.id}>
                  {resending === p.id ? <CircularProgress size={13} color="inherit" /> : <MarkEmailReadIcon sx={{ fontSize: 16 }} />}
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
        <TableCell>
          <Typography variant="caption" color="text.secondary">
            {p.created_at ? new Date(p.created_at).toLocaleDateString('sq-AL') : '—'}
          </Typography>
        </TableCell>
        <TableCell>
          <Stack direction="row" gap={0.3}>
            {st === 'pending' && isAdmin && (
              <>
                <Tooltip title={t('remoteWork.approveRequest')}>
                  <IconButton size="small" color="success" onClick={() => handleApprove(p)} disabled={saving}>
                    <ThumbUpIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={t('remoteWork.rejectRequest')}>
                  <IconButton size="small" color="error" onClick={() => { setRejectDialog(p); setRejectReason(''); }}>
                    <ThumbDownIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {!['revoked','rejected','pending'].includes(st) && isAdmin && (
              <Tooltip title={t('remoteWork.editPermit')}>
                <IconButton size="small" color="primary" onClick={() => openEdit(p)}>
                  <EditIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
            {!['revoked','rejected'].includes(st) && isAdmin && (
              <Tooltip title={t('remoteWork.revokePermit')}>
                <IconButton size="small" color="error" onClick={() => handleRevoke(p)}>
                  <DeleteOutlineIcon fontSize="small" />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </TableCell>
      </TableRow>
    );
  };

  // ── Tab config (segmented control) ──────────────────────────────────────────
  const tabs = [
    { value: 0, label: t('remoteWork.tabActive',  'Aktive'), count: activePermits.length },
    { value: 1, label: t('remoteWork.tabPending', 'Në pritje'), count: pendingCount },
    { value: 2, label: t('common.all', 'Të gjitha'), count: allPermits.length },
  ];

  return (
    <Box>
      <Stack spacing={{ xs: 2, md: 2.5 }}>

        {/* ── 1. Page header ──────────────────────────────────────────────── */}
        <Stack direction={{ xs: 'column', md: 'row' }} alignItems={{ md: 'center' }} justifyContent="space-between" spacing={1.5}>
          <Box>
            <Typography sx={{ fontWeight: 700, fontSize: { xs: '1.5rem', md: '2rem' }, letterSpacing: '-0.025em', color: 'text.primary', lineHeight: 1.1 }}>
              {t('remoteWork.title', 'Puna Hibride')}
            </Typography>
            <Typography sx={{ color: 'text.secondary', mt: 0.5, fontSize: '0.9375rem' }}>
              {todayCount} duke punuar nga distanca sot · {pendingCount} kërkesa në pritje · {permits.length} gjithsej
            </Typography>
          </Box>
          <Stack direction="row" spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
            <Button
              variant="outlined"
              startIcon={<WorkIcon sx={{ fontSize: '15px !important' }} />}
              onClick={() => setRequestOpen(true)}
              sx={{ height: 36, borderRadius: 1.5, px: 1.5, fontSize: '0.8125rem' }}
            >
              {t('remoteWork.requestLeave', 'Kërko leje')}
            </Button>
            {isAdmin && (
              <Button
                variant="contained"
                startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                onClick={() => setCreateOpen(true)}
                sx={{ height: 36, borderRadius: 1.5, px: 1.75, fontSize: '0.8125rem' }}
              >
                {t('remoteWork.newPermit', 'Leje e re')}
              </Button>
            )}
          </Stack>
        </Stack>

        {/* ── Snack ─────────────────────────────────────────────────────────── */}
        {snack && (
          <Alert severity={snackErr ? 'error' : 'success'} sx={{ borderRadius: 1.5 }} onClose={() => setSnack('')}>
            {snack}
          </Alert>
        )}

        {/* ── 2. Compact KPI strip ────────────────────────────────────────── */}
        <Grid container spacing={{ xs: 1.5, md: 2 }}>
          {[
            { label: 'Aktivë sot',       value: todayCount,     hint: 'duke punuar nga distanca tani',                                 tone: 'success', icon: HomeWorkIcon },
            { label: 'Leje aktive',      value: activeCount,    hint: 'me data të vlefshme përpara',                                   tone: 'primary', icon: CheckCircleIcon },
            { label: 'Në pritje',        value: pendingCount,   hint: pendingCount > 0 ? 'kërkojnë miratimin tënd' : 'asnjë në radhë', tone: pendingCount > 0 ? 'warning' : 'success', icon: HourglassEmptyIcon },
            { label: 'Total kërkesa',    value: permits.length, hint: 'historik gjithsej',                                             tone: 'primary', icon: EventIcon },
          ].map(k => {
            const Icon = k.icon;
            return (
              <Grid item xs={6} md={3} key={k.label}>
                <Paper elevation={0} sx={{
                  p: 1.75, borderRadius: 2, border: 1, borderColor: 'divider',
                  height: '100%', display: 'flex', flexDirection: 'column', gap: 0.5,
                  transition: 'border-color 0.15s ease',
                  '&:hover': { borderColor: 'grey.300' },
                }}>
                  <Stack direction="row" alignItems="center" spacing={0.75}>
                    <Box sx={{
                      width: 22, height: 22, borderRadius: 1,
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      bgcolor: `${k.tone}.soft`, color: `${k.tone}.main`,
                    }}>
                      <Icon sx={{ fontSize: 13 }} />
                    </Box>
                    <Typography sx={{ fontSize: '0.75rem', fontWeight: 500, color: 'text.secondary' }}>
                      {k.label}
                    </Typography>
                  </Stack>
                  <Typography sx={{
                    fontSize: { xs: '1.375rem', md: '1.5rem' }, fontWeight: 700,
                    color: 'text.primary', lineHeight: 1.1, letterSpacing: '-0.025em',
                    fontVariantNumeric: 'tabular-nums',
                  }}>
                    {k.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.6875rem', color: 'text.disabled', fontWeight: 500 }} noWrap>
                    {k.hint}
                  </Typography>
                </Paper>
              </Grid>
            );
          })}
        </Grid>

        {/* ── 3. Segmented tabs + section heading ─────────────────────────── */}
        <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Box sx={{
            display: 'inline-flex',
            border: 1, borderColor: 'divider',
            borderRadius: 1.5,
            bgcolor: 'background.paper',
            p: 0.375,
          }}>
            {tabs.map(t => {
              const active = tab === t.value;
              return (
                <Box
                  key={t.value}
                  onClick={() => setTab(t.value)}
                  sx={{
                    px: 1.5, py: 0.625, borderRadius: 1,
                    fontSize: '0.8125rem', fontWeight: 600,
                    cursor: 'pointer',
                    display: 'inline-flex', alignItems: 'center', gap: 0.75,
                    bgcolor: active ? 'primary.soft' : 'transparent',
                    color: active ? 'primary.dark' : 'text.secondary',
                    transition: 'background-color 0.12s ease, color 0.12s ease',
                    '&:hover': { bgcolor: active ? 'primary.soft' : 'grey.50' },
                  }}
                >
                  {t.label}
                  <Box component="span" sx={{
                    fontSize: '0.6875rem', fontWeight: 600,
                    px: 0.75, py: 0,
                    borderRadius: 999,
                    bgcolor: active ? 'background.paper' : 'grey.100',
                    color: active ? 'primary.dark' : 'text.disabled',
                    minWidth: 18, textAlign: 'center',
                  }}>
                    {t.count}
                  </Box>
                </Box>
              );
            })}
          </Box>
          {visibleList.length > 0 && !loading && (
            <Typography variant="caption" sx={{ color: 'text.secondary' }}>
              {visibleList.length} {visibleList.length === 1 ? 'rezultat' : 'rezultate'}
            </Typography>
          )}
        </Stack>

        {/* ── 4. Table (or empty state) ───────────────────────────────────── */}
        <Paper elevation={0} sx={{ borderRadius: 2, border: 1, borderColor: 'divider', overflow: 'hidden' }}>
          {loading ? (
            <Box sx={{ py: 6, textAlign: 'center' }}>
              <CircularProgress size={28} />
            </Box>
          ) : visibleList.length === 0 ? (
            <Box sx={{ py: 6, textAlign: 'center', px: 3 }}>
              <Box sx={{ width: 56, height: 56, borderRadius: 2, bgcolor: 'primary.soft', color: 'primary.main', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', mb: 1.5 }}>
                <HomeWorkIcon sx={{ fontSize: 28 }} />
              </Box>
              <Typography sx={{ fontSize: '0.9375rem', fontWeight: 600, color: 'text.primary', mb: 0.5 }}>
                {tab === 1
                  ? t('remoteWork.noPending', 'Asnjë kërkesë në pritje')
                  : t('remoteWork.noPermits', 'Asnjë leje pune nga distanca ende')}
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                {tab === 1
                  ? 'Kur punëtorët dorëzojnë kërkesa, do të shfaqen këtu për miratim.'
                  : 'Krijo lejen e parë ose kërko leje për punë nga distanca.'}
              </Typography>
              <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap" rowGap={1}>
                <Button
                  variant="outlined"
                  startIcon={<WorkIcon sx={{ fontSize: '15px !important' }} />}
                  onClick={() => setRequestOpen(true)}
                  sx={{ height: 36, borderRadius: 1.5 }}
                >
                  {t('remoteWork.requestLeave', 'Kërko leje')}
                </Button>
                {isAdmin && (
                  <Button
                    variant="contained"
                    startIcon={<AddIcon sx={{ fontSize: '16px !important' }} />}
                    onClick={() => setCreateOpen(true)}
                    sx={{ height: 36, borderRadius: 1.5 }}
                  >
                    {t('remoteWork.newPermit', 'Leje e re')}
                  </Button>
                )}
              </Stack>
            </Box>
          ) : (
            <Table>
              <TableHead>
                <TableRow>
                  {[
                    t('remoteWork.tableWorker',       'Punëtori'),
                    t('remoteWork.tableAllowedDates', 'Datat'),
                    t('remoteWork.tableStatus',       'Statusi'),
                    t('remoteWork.tableApprovedBy',   'Miratuar nga'),
                    t('remoteWork.tableNotes',        'Shënime'),
                    t('remoteWork.tableEmail',        'Email'),
                    t('remoteWork.tableCreated',      'Krijuar'),
                    '',
                  ].map(h => (
                    <TableCell key={h} sx={{
                      fontWeight: 600, fontSize: '0.6875rem', letterSpacing: '0.04em',
                      textTransform: 'uppercase', color: 'text.disabled',
                      py: 1.25, bgcolor: 'background.paper',
                      borderBottom: 1, borderColor: 'divider',
                      whiteSpace: 'nowrap',
                    }}>{h}</TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleList.map(p => <PermitRow key={p.id} p={p} />)}
              </TableBody>
            </Table>
          )}
        </Paper>
      </Stack>

      {/* ── Revoke Confirm Dialog ── */}
      <Dialog
        open={!!revokeTarget}
        onClose={() => !revoking && setRevokeTarget(null)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: 3, overflow: 'hidden' } }}
      >
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            pt: 3.5,
            pb: 1,
          }}
        >
          <Box
            sx={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: 'error.lighter',
              background: (th) =>
                th.palette.mode === 'dark'
                  ? 'rgba(220, 38, 38, 0.16)'
                  : 'rgba(220, 38, 38, 0.10)',
              color: 'error.main',
            }}
          >
            <BlockIcon sx={{ fontSize: 30 }} />
          </Box>
        </Box>
        <DialogTitle
          sx={{
            textAlign: 'center',
            fontWeight: 700,
            fontSize: '1.05rem',
            pt: 1.5,
            pb: 0.5,
          }}
        >
          {t('remoteWork.revokeDialogTitle', 'Revoko lejen?')}
        </DialogTitle>
        <DialogContent sx={{ textAlign: 'center', pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.55 }}>
            {t(
              'remoteWork.revokeDialogBody',
              'Punëtori nuk do të mund të punojë nga distanca.'
            )}
          </Typography>
          {revokeTarget?.worker_name && (
            <Box
              sx={{
                mt: 2,
                mx: 'auto',
                px: 2,
                py: 1.25,
                maxWidth: 280,
                borderRadius: 2,
                bgcolor: 'action.hover',
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <PersonIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                {revokeTarget.worker_name}
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 0.5, gap: 1 }}>
          <Button
            fullWidth
            onClick={() => setRevokeTarget(null)}
            disabled={revoking}
            variant="outlined"
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              borderRadius: 2,
              py: 1,
            }}
          >
            {t('common.cancel', 'Anulo')}
          </Button>
          <Button
            fullWidth
            onClick={executeRevoke}
            disabled={revoking}
            variant="contained"
            color="error"
            startIcon={revoking ? <CircularProgress size={16} color="inherit" /> : <DeleteOutlineIcon />}
            sx={{
              textTransform: 'none',
              fontWeight: 700,
              borderRadius: 2,
              py: 1,
              boxShadow: 'none',
              '&:hover': { boxShadow: 'none' },
            }}
          >
            {revoking
              ? t('remoteWork.revoking', 'Po revokoj…')
              : t('remoteWork.revoke', 'Revoko')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Reject Dialog ── */}
      <Dialog open={!!rejectDialog} onClose={() => setRejectDialog(null)} maxWidth="sm" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <ThumbDownIcon color="error" /> {t('remoteWork.rejectDialogTitle')}
          <IconButton onClick={() => setRejectDialog(null)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Typography variant="body2" sx={{ mb: 2 }}>
            {t('remoteWork.rejectingFor')} <b>{rejectDialog?.worker_name}</b>.
          </Typography>
          <TextField fullWidth multiline minRows={2} label={t('remoteWork.rejectReasonLabel')} size="small"
            value={rejectReason} onChange={e => setRejectReason(e.target.value)}
            placeholder={t('remoteWork.rejectReasonPlaceholder')} />
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setRejectDialog(null)} variant="outlined" sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button variant="contained" color="error" onClick={handleReject} disabled={saving}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <ThumbDownIcon />}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            {saving ? t('remoteWork.rejecting') : t('remoteWork.reject')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Request Dialog (punëtori/lideri) ── */}
      <Dialog open={requestOpen} onClose={() => setRequestOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <WorkIcon color="primary" /> {t('remoteWork.requestRemoteLeave')}
          <IconButton onClick={() => setRequestOpen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {myWorker ? (
            <Grid container spacing={3}>
              <Grid item xs={12} md={5}>
                <Stack spacing={2.5}>
                  <Alert severity="info" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                    <b>{t('remoteWork.worker')}:</b> {myWorkerName}<br />
                    {(myWorker.Email || myWorker.email) && <span><b>Email:</b> {myWorker.Email || myWorker.email}</span>}
                  </Alert>
                  <TextField fullWidth multiline minRows={3} label={t('remoteWork.requestReasonLabel')} size="small"
                    value={reqNotes} onChange={e => setReqNotes(e.target.value)}
                    placeholder={t('remoteWork.requestReasonPlaceholder')} />
                  <Alert severity="warning" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                    {t('remoteWork.requestPendingApproval')}
                  </Alert>
                </Stack>
              </Grid>
              <Grid item xs={12} md={7}>
                <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <EventIcon fontSize="small" color="primary" /> {t('remoteWork.selectRequestedDates')}
                </Typography>
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                  {t('remoteWork.clickDaysRemote')}
                </Typography>
                <DateMultiPicker selected={reqDates} onChange={setReqDates} />
                {reqDates.length > 0 && (
                  <Alert severity="success" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5 }}>
                    {t('remoteWork.daysSelected', { count: reqDates.length })}
                  </Alert>
                )}
              </Grid>
            </Grid>
          ) : (
            <Stack spacing={2}>
              <Alert severity="info">
                {t('remoteWork.accountNotAutoLinked')}
              </Alert>
              <TextField
                select fullWidth size="small" label={t('remoteWork.whoAreYou')}
                value={selfPickerVal}
                onChange={e => setSelfPickerVal(e.target.value)}
              >
                <MenuItem value="">{t('remoteWork.selectYourName')}</MenuItem>
                {workers
                  .filter(w => isActiveWorker(w))
                  .map(w => {
                    const wid = String(w.Id || w.id);
                    const wname = `${w.FirstName || w.Name || w.name || ''} ${w.LastName || w.Surname || w.surname || ''}`.trim();
                    return <MenuItem key={wid} value={wid}>{wname}</MenuItem>;
                  })}
              </TextField>
              <Button variant="contained" disabled={!selfPickerVal} onClick={handleSelfIdentify} sx={{ textTransform: 'none', alignSelf: 'flex-start' }}>
                {t('remoteWork.confirmIdentity')}
              </Button>
            </Stack>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setRequestOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleRequest} disabled={saving || !reqDates.length || !myWorker}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 160 }}>
            {saving ? t('remoteWork.sending') : t('remoteWork.sendRequest')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Create Permit Dialog (admin only) ── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <HomeWorkIcon color="primary" /> {t('remoteWork.newHybridPermit')}
          <IconButton onClick={() => setCreateOpen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Stack spacing={2.5}>
                {/* Filiali dhe departamenti janë zgjedhës grupi, jo vetëm filtra:
                    prej tyre merret ekipi i tërë me një klikim. */}
                <Stack direction="row" spacing={1}>
                  <TextField select fullWidth size="small" label="Filiali"
                    value={fBranch} onChange={e => { setFBranch(e.target.value); }}>
                    <MenuItem value="">Të gjitha</MenuItem>
                    {branches.map(b => (
                      <MenuItem key={b.Id || b.id} value={String(b.Id || b.id)}>{b.Name || b.name}</MenuItem>
                    ))}
                  </TextField>
                  <TextField select fullWidth size="small" label="Departamenti"
                    value={fDept} onChange={e => { setFDept(e.target.value); }}>
                    <MenuItem value="">Të gjithë</MenuItem>
                    {departments.map(d => (
                      <MenuItem key={d.Id || d.id} value={String(d.Id || d.id)}>{d.Name || d.name}</MenuItem>
                    ))}
                  </TextField>
                </Stack>

                <TextField select fullWidth label={t('remoteWork.worker')} size="small"
                  SelectProps={{
                    multiple: true,
                    value: selWorkers,
                    onChange: (e) => setSelWorkers(
                      typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value,
                    ),
                    renderValue: (ids) => (ids.length === 1
                      ? wName(selectedWorkerObjs[0]) || '1 punëtor'
                      : `${ids.length} punëtorë të zgjedhur`),
                    MenuProps: { PaperProps: { sx: { maxHeight: 320 } } },
                  }}
                  InputProps={{ startAdornment: <PersonIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} /> }}>
                  {pickableWorkers.length === 0 && (
                    <MenuItem disabled value="">Asnjë punëtor për këtë filtër</MenuItem>
                  )}
                  {pickableWorkers.map(w => (
                    <MenuItem key={wId(w)} value={wId(w)}>
                      <Checkbox size="small" checked={selWorkers.includes(wId(w))} />
                      <ListItemText
                        primary={wName(w) || (w.Email || w.email || '—')}
                        secondary={w.Email || w.email || 'Pa email'}
                        primaryTypographyProps={{ fontSize: '0.86rem' }}
                        secondaryTypographyProps={{ fontSize: '0.72rem' }}
                      />
                    </MenuItem>
                  ))}
                </TextField>

                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button size="small" variant="outlined" disabled={!pickableWorkers.length}
                    onClick={() => setSelWorkers(pickableWorkers.map(wId))}
                    sx={{ textTransform: 'none' }}>
                    Zgjidh të gjithë ({pickableWorkers.length})
                  </Button>
                  <Button size="small" disabled={!selWorkers.length} onClick={() => setSelWorkers([])}
                    sx={{ textTransform: 'none' }}>
                    Pastro
                  </Button>
                </Stack>

                {selectedWorkerObjs.length > 0 && (
                  <Box>
                    <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
                      {selectedWorkerObjs.map(w => (
                        <Chip key={wId(w)} size="small" label={wName(w) || w.Email || w.email}
                          onDelete={() => toggleWorker(wId(w))} />
                      ))}
                    </Stack>
                    {/* Pa email leja krijohet, por askush nuk e merr vesh — thuhet para se të ruhet. */}
                    {sendEmail && selectedWorkerObjs.some(w => !(w.Email || w.email)) && (
                      <Alert severity="warning" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                        {selectedWorkerObjs.filter(w => !(w.Email || w.email)).length} punëtorë pa email — leja u krijohet, njoftimi jo.
                      </Alert>
                    )}
                  </Box>
                )}

                <TextField fullWidth multiline minRows={3} label={t('remoteWork.notesOptional')} size="small"
                  value={notes} onChange={e => setNotes(e.target.value)}
                  placeholder={t('remoteWork.notesPlaceholder')} />

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, p: 1.5, bgcolor: sendEmail ? 'primary.50' : 'grey.50', borderRadius: 1.5, border: '1px solid', borderColor: sendEmail ? 'primary.light' : 'divider', cursor: 'pointer' }}
                  onClick={() => setSendEmail(!sendEmail)}>
                  <EmailIcon color={sendEmail ? 'primary' : 'disabled'} fontSize="small" />
                  <Box sx={{ flex: 1 }}>
                    <Typography variant="body2" fontWeight={600}>{t('remoteWork.sendEmailNotification')}</Typography>
                    <Typography variant="caption" color="text.secondary">{t('remoteWork.emailNotificationDesc')}</Typography>
                  </Box>
                  <Chip size="small" label={sendEmail ? 'Po' : 'Jo'} color={sendEmail ? 'primary' : 'default'} />
                </Box>
              </Stack>
            </Grid>

            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon fontSize="small" color="primary" /> {t('remoteWork.selectAllowedDates')}
              </Typography>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
                {t('remoteWork.clickDaysAdmin')}
              </Typography>
              <DateMultiPicker selected={selDates} onChange={setSelDates} />
              {selDates.length > 0 && (
                <Alert severity="success" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5 }}>
                    {t('remoteWork.daysSelected', { count: selDates.length })}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setCreateOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          <Button variant="contained" onClick={handleCreate} disabled={saving || !selWorkers.length || !selDates.length}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SendIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 160 }}>
            {saving
              ? t('remoteWork.saving')
              : `${sendEmail ? t('remoteWork.createAndEmail') : t('remoteWork.createPermit')}${selWorkers.length > 1 ? ` (${selWorkers.length})` : ''}`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Permit Dialog ── */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="md" fullWidth PaperProps={{ sx: { borderRadius: 2.5 } }}>
        <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1, fontWeight: 700 }}>
          <EditIcon color="primary" /> {t('remoteWork.editPermitTitle')} — {editPermit?.worker_name}
          <IconButton onClick={() => setEditOpen(false)} sx={{ ml: 'auto' }}><CloseIcon /></IconButton>
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12} md={5}>
              <Stack spacing={2.5}>
                {editPermit?.worker_email && (
                  <Alert severity="info" sx={{ fontSize: '0.78rem', py: 0.5 }}>
                    <b>Email:</b> {editPermit.worker_email}
                  </Alert>
                )}
                <TextField fullWidth multiline minRows={3} label={t('remoteWork.notesOptional')} size="small"
                  value={editNotes} onChange={e => setEditNotes(e.target.value)}
                  placeholder={t('remoteWork.notesPlaceholder')} />
              </Stack>
            </Grid>
            <Grid item xs={12} md={7}>
              <Typography variant="subtitle2" fontWeight={700} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <EventIcon fontSize="small" color="primary" /> {t('remoteWork.changeAllowedDates')}
              </Typography>
              <DateMultiPicker selected={editDates} onChange={setEditDates} />
              {editDates.length > 0 && (
                <Alert severity="success" sx={{ mt: 1.5, fontSize: '0.78rem', py: 0.5 }}>
                    {t('remoteWork.daysSelected', { count: editDates.length })}
                </Alert>
              )}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2, gap: 1 }}>
          <Button onClick={() => setEditOpen(false)} variant="outlined" sx={{ textTransform: 'none' }}>{t('common.cancel')}</Button>
          {editPermit?.worker_email && (
            <Button variant="outlined" color="primary"
              onClick={() => { setEditOpen(false); handleResendEmail(editPermit); }}
              startIcon={<MarkEmailReadIcon />} sx={{ textTransform: 'none' }}>
              {t('remoteWork.resendEmailBtn')}
            </Button>
          )}
          <Button variant="contained" onClick={handleEdit} disabled={saving || !editDates.length}
            startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <EditIcon />}
            sx={{ textTransform: 'none', fontWeight: 600, minWidth: 140 }}>
            {saving ? t('remoteWork.saving') : t('remoteWork.saveChanges')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
